import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp, Activity, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function PokemonIndex() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowKey, setWindowKey] = useState("30");

  useEffect(() => {
    api.get("/index/pokemon")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-center text-white/40 font-mono py-32 tracking-widest text-xs">{t("common.loading")}</div>;
  }
  if (!data) return null;

  const snapshot = data.snapshots[windowKey] || {};
  const series = data.series_90d || [];
  const sparkData = series.map((s) => ({ date: s.date, value: s.volume }));

  const WINDOWS = [
    { k: "30", label: "30D", icon: Activity },
    { k: "90", label: "90D", icon: TrendingUp },
    { k: "365", label: "1Y", icon: Calendar },
  ];

  return (
    <div className="page-enter max-w-[1440px] mx-auto px-6 lg:px-10 py-12">
      <div className="mb-8">
        <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-2">// POKETRACKER_INDEX · LIVE</div>
        <h1 className="font-heading font-black text-5xl tracking-tighter">Pokémon Index</h1>
        <p className="text-white/50 mt-2 max-w-2xl">A composite indicator of trading-card market activity. Computed from community-reported sales: sum of (average price × sale count) across the top-50 most-active cards.</p>
      </div>

      {/* Window selector */}
      <div className="inline-flex items-center border border-white/15 p-0.5 mb-6" data-testid="index-window-toggle">
        {WINDOWS.map(({ k, label, icon: Icon }) => (
          <button
            key={k}
            onClick={() => setWindowKey(k)}
            data-testid={`index-window-${k}`}
            className={`flex items-center gap-1.5 px-4 py-2 text-xs font-heading font-semibold uppercase tracking-wider transition ${
              windowKey === k ? "bg-[#FACC15] text-black" : "text-white/70 hover:text-white"
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Big value + chart */}
      <div className="grid lg:grid-cols-[1fr,1.4fr] gap-6 mb-10">
        <div className="flat-card p-8" data-testid="index-value-card">
          <div className="font-mono text-xs text-white/40 uppercase tracking-widest mb-2">Index value · {snapshot.window_days}d window</div>
          <div className="font-heading font-black text-6xl tracking-tighter neon-yellow leading-none">
            {Number(snapshot.index_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
          <div className="font-mono text-xs text-white/40 mt-2 uppercase tracking-wider">PTI · POKETRACKER INDEX</div>
          <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-3 gap-3 text-center">
            {WINDOWS.map(({ k, label }) => {
              const s = data.snapshots[k] || {};
              return (
                <div key={k} className={`flat-card p-3 ${windowKey === k ? "border-[#FACC15]/40" : ""}`}>
                  <div className="font-mono text-[10px] text-white/40 tracking-wider">{label}</div>
                  <div className="font-mono font-bold text-base mt-1">{Number(s.index_value || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flat-card p-5" data-testid="index-chart-card">
          <div className="font-mono text-[10px] text-white/40 uppercase tracking-widest mb-3">// DAILY_VOLUME · LAST_90D (EUR)</div>
          {sparkData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={sparkData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <YAxis tick={{ fill: "#666", fontSize: 10, fontFamily: "JetBrains Mono" }} />
                <Tooltip contentStyle={{ background: "rgba(15,15,20,0.95)", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "JetBrains Mono", fontSize: 12 }} />
                <Line type="monotone" dataKey="value" stroke="#FACC15" strokeWidth={2} dot={{ fill: "#FACC15", r: 2 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-center text-white/40 py-20 font-mono text-xs tracking-widest">NO DATA</div>
          )}
        </div>
      </div>

      {/* Contributing cards */}
      <div>
        <div className="font-mono text-xs text-[#00E5FF] tracking-widest mb-3">// TOP_CONTRIBUTORS · {snapshot.window_days}D</div>
        <div className="flat-card overflow-hidden">
          {(snapshot.contributing_cards || []).length === 0 ? (
            <div className="p-10 text-center text-white/40 text-sm">Not enough sales data yet for this window.</div>
          ) : (
            <ul className="divide-y divide-white/10">
              {(snapshot.contributing_cards || []).map((c, i) => (
                <li key={c.card_id} className="px-5 py-3 grid grid-cols-[40px,48px,1fr,auto,auto] gap-4 items-center text-sm">
                  <span className="font-mono text-xl font-bold text-white/30">{String(i + 1).padStart(2, "0")}</span>
                  <Link to={`/cards/${c.card_id}`} className="w-12 h-16 bg-black/40 border border-white/10 overflow-hidden">
                    {c.card?.image && <img src={c.card.image} alt={c.card.name} className="w-full h-full object-cover" loading="lazy" />}
                  </Link>
                  <Link to={`/cards/${c.card_id}`} className="min-w-0 hover:text-[#FACC15]">
                    <div className="font-heading font-bold text-base tracking-tight truncate">{c.card?.name || c.card_id}</div>
                    <div className="font-mono text-[10px] text-white/40 tracking-wider truncate">{c.card?.set_name}</div>
                  </Link>
                  <div className="text-right">
                    <div className="font-mono text-[10px] text-white/40 uppercase tracking-wider">avg × n</div>
                    <div className="font-mono">{c.avg_price} × {c.count}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] text-white/40 uppercase tracking-wider">score</div>
                    <div className="font-mono font-bold text-[#FACC15]">{c.score}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <p className="text-center text-white/30 font-mono text-[10px] tracking-widest mt-10">
        // COMPUTED_AT · {new Date(data.computed_at).toLocaleString()}
      </p>
    </div>
  );
}
